import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, X, Check, Landmark } from "lucide-react";
import { money } from "../lib/calc";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const empty = { descripcion: "", monto_total: "", abono_semanal: "" };
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

export default function InstallmentLoansDialog({ entry, weekId, onClose, onChanged }) {
  const open = !!entry;
  const workerId = entry?.worker_id;
  const [loans, setLoans] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!workerId) return;
    try { const { data } = await api.get(`/workers/${workerId}/loans`); setLoans(data); }
    catch (e) { err(e); }
  }, [workerId]);

  useEffect(() => { if (open) { load(); setForm(empty); setEditing(null); } }, [open, load]);

  const reset = () => { setForm(empty); setEditing(null); };

  const submit = async () => {
    if (!(Number(form.monto_total) > 0) && !(Number(form.abono_semanal) > 0))
      return toast.error("Captura el monto total y el abono semanal");
    const payload = {
      descripcion: form.descripcion,
      monto_total: Number(form.monto_total) || 0,
      abono_semanal: Number(form.abono_semanal) || 0,
    };
    try {
      if (editing) await api.put(`/loans/${editing}`, payload);
      else await api.post(`/workers/${workerId}/loans`, payload);
      reset(); await load(); onChanged();
      toast.success(editing ? "Préstamo actualizado" : "Préstamo agregado");
    } catch (e) { err(e); }
  };

  const startEdit = (l) => {
    setEditing(l.id);
    setForm({ descripcion: l.descripcion || "", monto_total: l.monto_total || "", abono_semanal: l.abono_semanal || "" });
  };

  const remove = async (id) => {
    try { await api.delete(`/loans/${id}`); await load(); onChanged(); toast.success("Préstamo eliminado"); }
    catch (e) { err(e); }
  };

  const toggleWeek = async (l, apply) => {
    try { await api.post(`/loans/${l.id}/apply`, { week_id: weekId, apply }); await load(); onChanged(); }
    catch (e) { err(e); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="loans-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Landmark size={18} /> Préstamos a plazos — {entry?.worker_name}
          </DialogTitle>
          <DialogDescription>
            Registra préstamos con abono semanal. Activa "Descontar esta semana" para reflejar el abono en la nómina del periodo seleccionado.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-2 items-end border border-slate-200 p-3 bg-slate-50 rounded-sm">
          <div className="col-span-5">
            <Label className="text-xs">Descripción (opcional)</Label>
            <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} data-testid="loan-desc" className="h-8" placeholder="Ej. Adelanto herramienta" />
          </div>
          <div className="col-span-3">
            <Label className="text-xs">Monto total</Label>
            <Input type="number" value={form.monto_total} onChange={(e) => setForm({ ...form, monto_total: e.target.value })} data-testid="loan-monto" className="h-8 font-mono-data" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Abono sem.</Label>
            <Input type="number" value={form.abono_semanal} onChange={(e) => setForm({ ...form, abono_semanal: e.target.value })} data-testid="loan-abono" className="h-8 font-mono-data" />
          </div>
          <div className="col-span-2 flex gap-1 justify-end">
            {editing && <Button variant="ghost" size="sm" onClick={reset} className="h-8 px-2"><X size={14} /></Button>}
            <Button size="sm" onClick={submit} data-testid="loan-save" className="h-8 bg-violet-600 hover:bg-violet-700">
              {editing ? <Check size={14} /> : <Plus size={14} />}
            </Button>
          </div>
        </div>

        <div className="max-h-80 overflow-auto border border-slate-200 rounded-sm divide-y divide-slate-100">
          {loans.length === 0 && <p className="p-4 text-center text-slate-400 text-sm">Sin préstamos a plazos registrados</p>}
          {loans.map((l) => {
            const applied = (l.applied_weeks || []).includes(weekId);
            return (
              <div key={l.id} className="p-3" data-testid={`loan-item-${l.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-slate-800 text-sm">{l.descripcion || "Préstamo"}</span>
                    {l.liquidado && <span className="ml-2 text-[10px] uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">Liquidado</span>}
                  </div>
                  <div className="whitespace-nowrap">
                    <button onClick={() => startEdit(l)} className="text-slate-500 hover:text-violet-600 mr-2" data-testid={`loan-edit-${l.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(l.id)} className="text-slate-500 hover:text-red-600" data-testid={`loan-del-${l.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-2 text-xs font-mono-data">
                  <div><span className="text-slate-400 block">Monto total</span>{money(l.monto_total)}</div>
                  <div><span className="text-slate-400 block">Abono semanal</span>{money(l.abono_semanal)}</div>
                  <div><span className="text-slate-400 block">Pagos</span>{l.pagos_realizados}</div>
                  <div><span className="text-slate-400 block">Saldo</span><span className={l.saldo <= 0 ? "text-emerald-700" : "text-slate-800"}>{money(l.saldo)}</span></div>
                </div>
                <div className="flex items-center gap-2 mt-2.5 bg-violet-50 rounded-sm px-2.5 py-1.5">
                  <Switch checked={applied} onCheckedChange={(v) => toggleWeek(l, v)} data-testid={`loan-apply-${l.id}`} />
                  <span className="text-xs text-slate-700">
                    Descontar {money(l.abono_semanal)} en esta semana
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
